import { FuturesOrderHistory, PositionSide } from '@kwenta/sdk/types'
import { formatCurrency, formatDollars, getDisplayAsset, truncateAddress } from '@kwenta/sdk/utils'
import { wei } from '@kwenta/wei'
import { FC, memo, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { FlexDivCol } from 'components/layout/flex'
import { DesktopOnlyView, MobileOrTabletView } from 'components/Media'
import Table, { TableHeader, TableNoResults } from 'components/Table'
import { TableCell } from 'components/Table/TableBodyRow'
import { Body } from 'components/Text'
import { NO_VALUE } from 'constants/placeholder'
import { blockExplorer } from 'containers/Connector/Connector'
import DashboardHistoryTableFilters from 'sections/dashboard/History/HistoryTableFilters'
import { selectPerpsProvider } from 'state/futures/common/selectors'
import { selectUserInfoOrderHistory } from 'state/futures/selectors'
import { selectSmartMarginQueryStatuses } from 'state/futures/snxPerpsV2/selectors'
import { selectCrossMarginQueryStatuses } from 'state/futures/snxPerpsV3/selectors'
import { PerpsProvider } from 'state/futures/types'
import { useAppSelector } from 'state/hooks'
import { FetchStatus } from 'state/types'
import { ExternalLink } from 'styles/common'

import MobileTableFilters from '../MobileTrade/UserTabs/TableFilters'
import PositionType from '../PositionType'
import { DEFAULT_TABLE_PAGE_SIZE } from '../styles'
import TimeDisplay from '../Trades/TimeDisplay'
import UserInfoTableFilters from '../UserInfo/TableFilters/TableFilters'
import { FuturesTab } from '../UserInfo/UserInfoTabs'

type OrderHistoryTableProps = {
	filter?: 'dashboard' | 'userInfo'
	pagination?: boolean
	rounded?: boolean
	noBottom?: boolean
}

const OrderHistoryTable: FC<OrderHistoryTableProps> = memo(
	({ filter = 'userInfo', pagination = false, rounded = false, noBottom = false }) => {
		const { t } = useTranslation()
		const futuresType = useAppSelector(selectPerpsProvider)
		const orderHistory = useAppSelector(selectUserInfoOrderHistory)

		const { orderHistory: cmOrderHistory } = useAppSelector(selectCrossMarginQueryStatuses)
		const { orderHistory: smOrderHistory } = useAppSelector(selectSmartMarginQueryStatuses)

		const isLoading = useMemo(() => {
			const orderHistoryStatus =
				futuresType === PerpsProvider.SNX_V3 ? cmOrderHistory.status : smOrderHistory.status
			return !orderHistory.length && orderHistoryStatus === FetchStatus.Loading
		}, [orderHistory, cmOrderHistory, smOrderHistory, futuresType])

		const columnsDeps = useMemo(() => [orderHistory], [orderHistory])

		const formatAmount = useCallback(
			(order: FuturesOrderHistory) => {
				if (!order.sizeDelta || !order.displayAsset) return '-'

				const size = wei(order.sizeDelta).abs()
				const fullPositionText = t('futures.market.user.order-history.full-position')

				const formattedAmount = formatCurrency(order.displayAsset, size, {
					currencyKey: getDisplayAsset(order.displayAsset) ?? '',
					suggestDecimals: true,
				})

				if (order.isFullPosition) {
					return fullPositionText
				}

				return formattedAmount
			},
			[t]
		)

		const formatExecuted = useCallback(
			(order: FuturesOrderHistory) => {
				if (!order.executed || !order.displayAsset) return '-'

				const fullPositionText = t('futures.market.user.order-history.full-position')
				const formattedExecuted = formatDollars(wei(order.executed))

				if (futuresType === PerpsProvider.SNX_V3) {
					return formattedExecuted
				}

				if (order.isFullPosition) {
					return fullPositionText
				}

				return formattedExecuted
			},
			[t, futuresType]
		)

		const formatTriggerConditions = useCallback(
			(order: FuturesOrderHistory) => {
				if (futuresType === PerpsProvider.SNX_V2) {
					return formatDollars(wei(order.triggerConditions))
				}

				if (!order.triggerConditions) return '-'

				const triggerConditions = wei(order.triggerConditions)
				const side = triggerConditions.lt(0) ? ' <= ' : ' >= '

				return (
					t('futures.market.user.order-history.market') +
					side +
					formatDollars(triggerConditions.abs())
				)
			},
			[futuresType, t]
		)

		return (
			<Container>
				<DesktopOnlyView>
					<TableContainer>
						{filter === 'userInfo' ? (
							<UserInfoTableFilters tab={FuturesTab.ORDER_HISTORY} showFilterType />
						) : (
							<DashboardHistoryTableFilters />
						)}
						<Table
							highlightRowsOnHover
							rounded={rounded}
							noBottom={noBottom}
							showPagination={pagination}
							pageSize={pagination ? DEFAULT_TABLE_PAGE_SIZE : undefined}
							columns={[
								{
									header: () => (
										<TableHeader>{t('futures.market.user.order-history.date')}</TableHeader>
									),
									accessorKey: 'timestamp',
									cell: (cellProps) => <TimeDisplay value={cellProps.getValue()} />,
									enableSorting: true,
								},
								{
									header: () => (
										<TableHeader>{t('futures.market.user.order-history.asset')}</TableHeader>
									),
									accessorKey: 'displayAsset',
									cell: (cellProps) => (
										<CellBody order={cellProps.row.original}>{cellProps.getValue()}</CellBody>
									),
									enableSorting: true,
								},
								{
									header: () => (
										<TableHeader>{t('futures.market.user.order-history.type')}</TableHeader>
									),
									accessorKey: 'orderType',
									cell: (cellProps) => (
										<OrderType order={cellProps.row.original}>{cellProps.getValue()}</OrderType>
									),
									enableSorting: true,
								},
								{
									header: () => (
										<TableHeader>{t('futures.market.user.order-history.side')}</TableHeader>
									),
									accessorKey: 'side',
									cell: (cellProps) => {
										return <PositionType side={cellProps.row.original.side ?? PositionSide.LONG} />
									},
									enableSorting: true,
								},
								{
									header: () => (
										<TableHeader>
											{t('futures.market.user.order-history.trigger-conditions')}
										</TableHeader>
									),
									accessorKey: 'triggerConditions',
									cell: (cellProps) => (
										<CellBody order={cellProps.row.original}>
											{formatTriggerConditions(cellProps.row.original)}
										</CellBody>
									),
									enableSorting: true,
								},
								{
									header: () => (
										<TableHeader>{t('futures.market.user.order-history.executed')}</TableHeader>
									),
									accessorKey: 'executed',
									cell: (cellProps) => (
										<CellBody order={cellProps.row.original}>
											{formatExecuted(cellProps.row.original)}
										</CellBody>
									),
									enableSorting: true,
								},
								{
									header: () => (
										<TableHeader>{t('futures.market.user.order-history.amount')}</TableHeader>
									),
									accessorKey: 'sizeDelta',
									cell: (cellProps) => (
										<CellBody order={cellProps.row.original}>
											{formatAmount(cellProps.row.original)}
										</CellBody>
									),
									enableSorting: true,
								},
								{
									header: () => (
										<TableHeader>{t('futures.market.user.order-history.status')}</TableHeader>
									),
									accessorKey: 'status',
									cell: (cellProps) => {
										return (
											<OrderStatus order={cellProps.row.original}>
												{cellProps.getValue()}
											</OrderStatus>
										)
									},
									enableSorting: true,
								},
								{
									header: () => (
										<TableHeader>{t('futures.market.user.order-history.transaction')}</TableHeader>
									),
									accessorKey: 'txnHash',
									cell: (cellProps) => {
										return (
											<Body>
												{cellProps.getValue() ? (
													<StyledExternalLink href={blockExplorer.txLink(cellProps.getValue())}>
														{truncateAddress(cellProps.getValue())}
													</StyledExternalLink>
												) : (
													NO_VALUE
												)}
											</Body>
										)
									},
								},
							]}
							columnsDeps={columnsDeps}
							data={orderHistory}
							isLoading={isLoading && !orderHistory?.length}
							onTableRowClick={(row) =>
								row.original.txnHash
									? window.open(
											blockExplorer.txLink(row.original.txnHash),
											'_blank',
											'noopener noreferrer'
									  )
									: undefined
							}
							noResultsMessage={
								!isLoading && orderHistory?.length === 0 ? (
									<TableNoResults>
										{t('futures.market.user.order-history.no-results')}
									</TableNoResults>
								) : undefined
							}
						/>
					</TableContainer>
				</DesktopOnlyView>

				<MobileOrTabletView>
					<MobileTableContainer>
						{filter === 'userInfo' && <MobileTableFilters showFilters />}
						<MobileTable
							highlightRowsOnHover
							rounded={false}
							noBottom={false}
							autoResetPageIndex={false}
							showPagination={false}
							columns={[
								{
									accessorKey: 'displayAsset',
									cell: (cellProps) => (
										<FlexDivCol>
											<CellBody order={cellProps.row.original}>{cellProps.getValue()}</CellBody>
											<OrderStatus order={cellProps.row.original}>
												{cellProps.row.original.status}
											</OrderStatus>
										</FlexDivCol>
									),
								},
								{
									accessorKey: 'side',
									cell: (cellProps) => (
										<FlexDivCol key={1}>
											<Body color="secondary">{t('futures.market.user.order-history.side')}</Body>
											<PositionType side={cellProps.row.original.side ?? PositionSide.LONG} />
										</FlexDivCol>
									),
								},
								{
									accessorKey: 'amount',
									cell: (cellProps) => (
										<FlexDivCol>
											<Body color="secondary">{t('futures.market.user.order-history.amount')}</Body>
											<CellBody order={cellProps.row.original}>
												{formatAmount(cellProps.row.original)}
											</CellBody>
										</FlexDivCol>
									),
								},
								{
									accessorKey: 'orderType',
									cell: (cellProps) => (
										<FlexDivCol>
											<Body color="secondary">{t('futures.market.user.order-history.type')}</Body>
											<OrderType order={cellProps.row.original}>{cellProps.getValue()}</OrderType>
										</FlexDivCol>
									),
								},
								{
									accessorKey: 'triggerConditions',
									cell: (cellProps) => (
										<FlexDivCol>
											<Body color="secondary">
												{t('futures.market.user.order-history.trigger-conditions')}
											</Body>
											<CellBody order={cellProps.row.original}>
												{formatTriggerConditions(cellProps.row.original)}
											</CellBody>
										</FlexDivCol>
									),
								},
								{
									accessorKey: 'executed',
									cell: (cellProps) => (
										<FlexDivCol>
											<Body color="secondary">
												{t('futures.market.user.order-history.executed')}
											</Body>
											<CellBody order={cellProps.row.original}>
												{formatExecuted(cellProps.row.original)}
											</CellBody>
										</FlexDivCol>
									),
								},
								{
									accessorKey: 'timestamp',
									cell: (cellProps) => (
										<FlexDivCol>
											<TimeDisplay value={cellProps.getValue()} />
										</FlexDivCol>
									),
								},
								{
									accessorKey: 'txnHash',
									cell: (cellProps) => (
										<FlexDivCol>
											<Body color="secondary">
												{t('futures.market.user.order-history.transaction')}
											</Body>
											<Body>
												{cellProps.getValue() ? (
													<StyledExternalLink href={blockExplorer.txLink(cellProps.getValue())}>
														{truncateAddress(cellProps.getValue())}
													</StyledExternalLink>
												) : (
													NO_VALUE
												)}
											</Body>
										</FlexDivCol>
									),
								},
							]}
							columnsDeps={columnsDeps}
							data={orderHistory}
							isLoading={isLoading && !orderHistory?.length}
							onTableRowClick={(row) =>
								row.original.txnHash
									? window.open(
											blockExplorer.txLink(row.original.txnHash),
											'_blank',
											'noopener noreferrer'
									  )
									: undefined
							}
							noResultsMessage={
								!isLoading && orderHistory?.length === 0 ? (
									<TableNoResults>
										{t('futures.market.user.order-history.no-results')}
									</TableNoResults>
								) : undefined
							}
						/>
					</MobileTableContainer>
				</MobileOrTabletView>
			</Container>
		)
	}
)

export default OrderHistoryTable

const Container = styled.div`
	display: flex;
	flex-direction: row;
	flex: 1;
	height: 100%;
`
const MobileTableContainer = styled(FlexDivCol)`
	width: 100%;
`
const MobileTable = styled(Table)`
	.table-row:first-child {
		display: none;
	}

	.table-body {
		max-height: 100%;
	}

	.table-body-row:first-child {
		padding-top: 15px;
	}

	.table-body-row {
		display: grid;
		grid-template-columns: 1fr 1fr;
		grid-template-rows: auto;
		grid-column-gap: 15px;
		grid-row-gap: 10px;
		padding: 15px 0px;
		margin: 0 20px;
	}

	${TableCell}:first-child,
	${TableCell}:last-child {
		padding: 0;
	}
	${TableCell} {
		height: 100%;
		width: 100% !important;
		display: flex;
		align-items: start;
		justify-content: space-between;
	}
	border: none;
` as typeof Table

const TableContainer = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100%;

	.table-body-row {
		border-bottom: none;
	}
`

const StyledExternalLink = styled(ExternalLink)`
	color: ${(props) => props.theme.colors.selectedTheme.button.text.primary};
	text-decoration: underline;
	&:hover {
		text-decoration: underline;
	}
`

const CellBody = styled(Body)<{ order: FuturesOrderHistory }>`
	color: ${(props) =>
		props.theme.colors.selectedTheme.newTheme.text[
			props.order.status === 'Filled' ? 'primary' : 'secondary'
		]};
`

const OrderType = styled(Body)<{ order: FuturesOrderHistory }>`
	color: ${(props) =>
		props.theme.colors.selectedTheme.newTheme.text[
			props.order.orderType === 'Liquidation'
				? 'negative'
				: props.order.status === 'Filled'
				? 'primary'
				: 'secondary'
		]};
`

const OrderStatus = styled(Body)<{ order: FuturesOrderHistory }>`
	color: ${(props) =>
		props.theme.colors.selectedTheme.newTheme.text[
			props.order.status === 'Filled' ? 'positive' : 'secondary'
		]};
`
